//new code in the evening

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
// eslint-disable-next-line no-unused-vars
import config from "../utils/config";
import servicesConfig from "../utils/servicesConfig";
import { extractFileText } from "../utils/extractFileText";
import { analyzeResume } from "../utils/analyzeResume";
import { generateAndDownloadReport } from "../utils/generateReport";
import ServiceSelectorModal from "../components/ServiceSelectorModal";
import AnalysisSelectModal from "../components/AnalysisSelectModal";
import PaymentModal from "../components/PaymentModal";
import { detectCareerMismatch } from "../utils/detectCareerMismatch";
import { usePricing } from "../hooks/usePricing";
import ScoreRing from "../components/ScoreRing";
import {
  saveTransaction,
  getPendingTransaction,
  // eslint-disable-next-line no-unused-vars
  getCompletedTransaction,
  markTransactionComplete,
  getAllAnalyses,
  getTransactionUsage,
  // eslint-disable-next-line no-unused-vars
  getPlanLimits,
  trackPageView,
  trackAnalysisStart,
  trackAnalysisError,
  trackReportDownload,
  trackServiceClick,
} from "../utils/transactions";
import { collection, addDoc, getDocs, serverTimestamp } from "firebase/firestore";
import Icon, { IconBox } from "../components/Icons";
import { db } from "../utils/firebase";

// Per-plan localised pricing for intro plan cards
// new code
function PlanPrice({ planId }) {
  const { usdDisplay, localDisplay } = usePricing(planId);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1 }}>
          {usdDisplay}
        </span>
        <span style={{ fontSize: 13, color: "#94a3b8" }}>one-time</span>
      </div>
      {localDisplay && (
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>≈ {localDisplay}</div>
      )}
    </div>
  );
}

// ── Helper ──
function getPlanName(planId) {
  return servicesConfig.plans.find(p => p.id === planId)?.name || planId || "Plan";
}

// ── STEPS from servicesConfig ──
const payPerUsePlan = servicesConfig.plans.find(p => p.id === "pay_per_use");

const STEPS = servicesConfig.steps.map((s, i) => ({
  ...s,
  num: String(s.num).padStart(2, "0"),
  label: i === 1 && payPerUsePlan ? `Pay ${payPerUsePlan.price}` : s.label,
}));

function normalizeScore(score) {
  if (!score) return "Fair";
  const s = score.toString().toLowerCase();
  if (s.includes("good") || s.includes("excellent") || s.includes("strong") || s.includes("high")) return "Good";
  if (s.includes("needs") || s.includes("poor") || s.includes("weak") || s.includes("low") || s.includes("improve")) return "Needs Work";
  return "Fair";
}

function normalizeResult(data) {
  if (!data) return null;
  const sections = (data.sections || data.section_feedback || data.sectionFeedback || []).map(s => ({
    name: s.name || s.section || s.title || "Section",
    score: normalizeScore(s.score || s.rating || s.grade || "Fair"),
    feedback: s.feedback || s.comment || s.suggestion || s.description || "",
  }));
  const rewrites = (data.rewrites || data.suggested_rewrites || data.rewrite_suggestions || []).map(r => ({
    section: r.section || r.category || "",
    before: r.before || r.original || r.current || "",
    after: r.after || r.improved || r.suggested || r.rewritten || "",
  }));
  return {
    ats_score: data.ats_score ?? data.atsScore ?? data.score ?? 0,
    match_rate: data.match_rate ?? data.matchRate ?? data.match ?? 0,
    missing_count: data.missing_count ?? data.missingCount ?? (data.missing_keywords || []).length ?? 0,
    summary: data.summary || data.overall_summary || data.overview || "",
    present_keywords: data.present_keywords || data.presentKeywords || data.found_keywords || [],
    missing_keywords: data.missing_keywords || data.missingKeywords || data.keywords_missing || [],
    sections,
    rewrites,
    top_recommendations: data.top_recommendations || data.recommendations || data.topRecommendations || [],
  };
}

function safeResult(data) {
  if (!data) return null;
  return {
    ats_score: data.ats_score || 0,
    match_rate: data.match_rate || 0,
    missing_count: data.missing_count || 0,
    summary: data.summary || "",
    present_keywords: Array.isArray(data.present_keywords) ? data.present_keywords : [],
    missing_keywords: Array.isArray(data.missing_keywords) ? data.missing_keywords : [],
    sections: Array.isArray(data.sections) ? data.sections : [],
    rewrites: Array.isArray(data.rewrites) ? data.rewrites : [],
    top_recommendations: Array.isArray(data.top_recommendations) ? data.top_recommendations : [],
  };
}

// eslint-disable-next-line no-unused-vars
function ResultDisplay({ result, onDownload }) {
  const scoreColor = result.ats_score >= 75 ? "#059669" : result.ats_score >= 50 ? "#d97706" : "#dc2626";
  const scoreBg = result.ats_score >= 75 ? "#ecfdf5" : result.ats_score >= 50 ? "#fffbeb" : "#fef2f2";

  return (
    <div style={{ animation: "fadeIn 0.5s ease-out" }}>
      <div className="db-metrics-3" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 20, marginBottom: 24 }}>
        {[
          { label: "ATS Score", value: result.ats_score, unit: "/100", color: scoreColor, bg: scoreBg },
          { label: "Keyword Match", value: `${result.match_rate}%`, unit: "match rate", color: "#475569", bg: "#f1f5f9" },
          { label: "Missing Keywords", value: result.missing_count, unit: "to optimize", color: "#dc2626", bg: "#fef2f2" },
        ].map(m => (
          <div key={m.label} style={{ background: m.bg, borderRadius: 16, padding: "20px", textAlign: "center", border: "1px solid rgba(0,0,0,0.05)" }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: m.color, fontWeight: 600, marginBottom: 8, opacity: 0.7 }}>{m.label}</div>
            <div style={{ fontSize: 36, fontWeight: 800, color: m.color, lineHeight: 1 }}>{m.value}</div>
            <div style={{ fontSize: 12, color: m.color, marginTop: 4, opacity: 0.6 }}>{m.unit}</div>
          </div>
        ))}
      </div>

      <div style={{ height: 10, background: "#e2e8f0", borderRadius: 5, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ height: "100%", width: `${result.ats_score}%`, background: `linear-gradient(90deg, ${scoreColor}, ${scoreColor}cc)`, borderRadius: 5 }} />
      </div>

      {result.summary && (
        <div style={{ background: "#eff6ff", borderLeft: "4px solid #2563eb", padding: "18px 20px", borderRadius: 10, marginBottom: 24, fontSize: 14, color: "#1e40af", lineHeight: 1.6 }}>
          {result.summary}
        </div>
      )}

      <div className="db-kw-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 24 }}>
        <div style={{ background: "#ffffff", borderRadius: 16, padding: "20px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
            <span>✅</span> Keywords Found
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {result.present_keywords.length > 0
              ? result.present_keywords.map(k => (
                  <span key={k} style={{ background: "#d1fae5", color: "#065f46", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{k}</span>
                ))
              : <span style={{ fontSize: 13, color: "#94a3b8" }}>No keywords detected</span>}
          </div>
        </div>
        <div style={{ background: "#ffffff", borderRadius: 16, padding: "20px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: "#1e293b", display: "flex", alignItems: "center", gap: 8 }}>
            <span>❌</span> Missing Keywords
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {result.missing_keywords.length > 0
              ? result.missing_keywords.map(k => (
                  <span key={k} style={{ background: "#fee2e2", color: "#dc2626", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{k}</span>
                ))
              : <span style={{ fontSize: 13, color: "#94a3b8" }}>No major gaps</span>}
          </div>
        </div>
      </div>

      {result.sections.length > 0 && (
        <div style={{ background: "#ffffff", borderRadius: 16, padding: "24px", marginBottom: 24, border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20, color: "#1e293b" }}>Section Feedback</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {result.sections.map((s, i) => {
              const c = s.score === "Good" ? "#059669" : s.score === "Fair" ? "#d97706" : "#dc2626";
              const b = s.score === "Good" ? "#ecfdf5" : s.score === "Fair" ? "#fffbeb" : "#fef2f2";
              return (
                <div key={i} style={{ paddingBottom: i < result.sections.length - 1 ? 16 : 0, borderBottom: i < result.sections.length - 1 ? "1px solid #e2e8f0" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, fontSize: 15, color: "#1e293b" }}>{s.name}</span>
                    <span style={{ background: b, color: c, fontSize: 11, padding: "2px 12px", borderRadius: 20, fontWeight: 600 }}>{s.score}</span>
                  </div>
                  <p style={{ fontSize: 13, color: "#475569", margin: 0, lineHeight: 1.6 }}>{s.feedback}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {result.rewrites.length > 0 && (
        <div style={{ background: "#ffffff", borderRadius: 16, padding: "24px", marginBottom: 24, border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20, color: "#1e293b" }}>Suggested Rewrites</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {result.rewrites.map((r, i) => (
              <div key={i}>
                {r.section && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{r.section}</div>
                )}
                <div style={{ fontSize: 13, color: "#475569", padding: "12px 16px", background: "#f8fafc", borderRadius: 10, marginBottom: 8, border: "1px solid #e2e8f0" }}>{r.before}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", margin: "8px 0" }}>↓ improved version</div>
                <div style={{ fontSize: 13, color: "#2563eb", padding: "12px 16px", background: "#eff6ff", borderRadius: 10, border: "1px solid #bfdbfe" }}>{r.after}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result.top_recommendations.length > 0 && (
        <div style={{ background: "#ffffff", borderRadius: 16, padding: "24px", marginBottom: 24, border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20, color: "#1e293b" }}>Top Recommendations</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {result.top_recommendations.map((r, i) => (
              <div key={i} style={{ padding: "12px 16px", background: "#f8fafc", borderRadius: 10, fontSize: 13, display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ color: "#2563eb", fontWeight: 700, minWidth: 24 }}>{i + 1}.</span>
                <span style={{ color: "#334155", lineHeight: 1.6 }}>{r}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {onDownload && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <button
            onClick={onDownload}
            style={{ padding: "12px 32px", background: "transparent", color: "#2563eb", border: "2px solid #2563eb", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#2563eb"; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#2563eb"; }}
          >
            ↓ Download Full Report
          </button>
        </div>
      )}
    </div>
  );
}

// ── Animated steps shown during analysis ──
// const ANALYSIS_STEPS = [
//   { icon: "📄", label: "Reading your resume" },
//   { icon: "🔍", label: "Scanning keywords" },
//   { icon: "🎯", label: "Calculating ATS score" },
//   { icon: "💡", label: "Generating recommendations" },
//   { icon: "✅", label: "Preparing your report" },
// ];

//NEW CODE 
const ANALYSIS_STEPS = [
  { icon: "doc",    label: "Reading your resume" },
  { icon: "search", label: "Scanning keywords" },
  { icon: "score",  label: "Calculating ATS score" },
  { icon: "light",  label: "Generating recommendations" },
  { icon: "check",  label: "Preparing your report" },
];


function AnalysisSteps() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep(prev => (prev < ANALYSIS_STEPS.length - 1 ? prev + 1 : prev));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
      {ANALYSIS_STEPS.map((s, i) => {
        const isDone = i < activeStep;
        const isActive = i === activeStep;
        return (
          <div key={s.label} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "10px 14px", borderRadius: 10,
            background: isDone ? "#f0fdf4" : isActive ? "#eff6ff" : "#f8fafc",
            border: `1px solid ${isDone ? "#bbf7d0" : isActive ? "#bfdbfe" : "#e2e8f0"}`,
            transition: "all 0.4s ease",
            opacity: i > activeStep + 1 ? 0.4 : 1,
          }}>
            {/* <div style={{
              width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isDone ? "#dcfce7" : isActive ? "#dbeafe" : "#f1f5f9",
              fontSize: isDone ? 14 : 16,
            }}>
              {isDone ? "✓" : s.icon}
            </div> */}

            {/* // NEW CODE */}
            <div style={{
              width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: isDone ? "#dcfce7" : isActive ? "#dbeafe" : "#f1f5f9",
              fontSize: 13, fontWeight: 700,
              color: isDone ? "#059669" : isActive ? "#2563eb" : "#94a3b8",
            }}>
              {isDone ? "✓" : (i + 1)}
            </div>
            <span style={{
              fontSize: 13, fontWeight: isActive ? 600 : 500,
              color: isDone ? "#059669" : isActive ? "#2563eb" : "#94a3b8",
              flex: 1,
            }}>
              {s.label}
            </span>
            {isActive && (
              <div style={{ display: "flex", gap: 3 }}>
                {[0,1,2].map(dot => (
                  <div key={dot} style={{
                    width: 5, height: 5, borderRadius: "50%", background: "#2563eb",
                    animation: `bounce 1s ease-in-out ${dot * 0.2}s infinite`,
                  }} />
                ))}
              </div>
            )}
            {isDone && (
              <span style={{ fontSize: 13, color: "#059669", fontWeight: 600 }}>Done</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Collapsible panel used inside HistoryCard ──
function CollapsiblePanel({ icon, title, badge, badgeColor, badgeBg, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", marginBottom: 10 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "13px 18px", background: open ? "#f8fafc" : "#ffffff",
          border: "none", cursor: "pointer", gap: 12, transition: "background 0.15s",
        }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.background = "#f8fafc"; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = "#ffffff"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <span style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{title}</span>
          {badge && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: badgeBg || "#f1f5f9", color: badgeColor || "#64748b", letterSpacing: "0.04em" }}>
              {badge}
            </span>
          )}
        </div>
        <span style={{ fontSize: 11, color: "#94a3b8", transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
      </button>
      {open && (
        <div style={{ padding: "16px 18px", borderTop: "1px solid #f1f5f9", background: "#ffffff", animation: "fadeIn 0.2s ease" }}>
          {children}
        </div>
      )}
    </div>
  );
}

function HistoryCard({ analysis, index, userName, onExpand, isExpanded }) {
  const r = safeResult(analysis);
  const scoreColor = r.ats_score >= 75 ? "#059669" : r.ats_score >= 50 ? "#d97706" : "#dc2626";
  const scoreBg   = r.ats_score >= 75 ? "#ecfdf5" : r.ats_score >= 50 ? "#fffbeb" : "#fef2f2";
  const date = analysis.createdAt?.seconds
    ? new Date(analysis.createdAt.seconds * 1000).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "Unknown date";

  function handleDownload() { generateAndDownloadReport(r, userName); }

  return (
    <div style={{ background: "#ffffff", borderRadius: 16, marginBottom: 16, overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: isExpanded ? "0 4px 20px rgba(0,0,0,0.06)" : "none", transition: "box-shadow 0.2s" }}>

      {/* ── Card header (always visible) ── */}
      <div
        className="db-hcard-header" style={{ padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", cursor: "pointer", background: isExpanded ? "#f8fafc" : "#ffffff", borderBottom: isExpanded ? "1px solid #e2e8f0" : "none", transition: "background 0.15s" }}
        onClick={onExpand}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* Score pill */}
          <div style={{ background: scoreBg, borderRadius: 12, padding: "10px 16px", textAlign: "center", minWidth: 76 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: scoreColor, fontWeight: 600 }}>Score</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{r.ats_score}</div>
          </div>

          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 3 }}>Analysis #{index}</div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>{date}</div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2563eb", display: "inline-block" }} />
                Match <strong style={{ color: "#1e293b" }}>{r.match_rate}%</strong>
              </span>
              <span style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />
                Missing <strong style={{ color: "#dc2626" }}>{r.missing_count}</strong>
              </span>
              {r.sections.length > 0 && (
                <span style={{ fontSize: 12, color: "#475569", display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#059669", display: "inline-block" }} />
                  <strong style={{ color: "#1e293b" }}>{r.sections.length}</strong> sections
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="db-hcard-actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={e => { e.stopPropagation(); handleDownload(); }}
            style={{ padding: "8px 18px", background: "transparent", color: "#475569", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "all 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.color = "#2563eb"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.color = "#475569"; }}
          >
            ↓ Download
          </button>
          <button
            onClick={e => { e.stopPropagation(); onExpand(); }}
            style={{ padding: "8px 18px", background: isExpanded ? "#2563eb" : "transparent", color: isExpanded ? "#fff" : "#475569", border: "1px solid", borderColor: isExpanded ? "#2563eb" : "#cbd5e1", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "all 0.15s" }}
          >
            {isExpanded ? "▲ Collapse" : "▼ View Details"}
          </button>
        </div>
      </div>

      {/* ── Expanded: collapsible panels ── */}
      {isExpanded && (
        <div style={{ padding: "20px 24px", background: "#f8fafc" }}>

          {/* Score bar */}
          {/* <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#64748b", marginBottom: 6 }}>
              <span>ATS Score</span>
              <span style={{ fontWeight: 700, color: scoreColor }}>{r.ats_score}/100</span>
            </div>
            <div style={{ height: 8, background: "#e2e8f0", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${r.ats_score}%`, background: `linear-gradient(90deg, ${scoreColor}, ${scoreColor}bb)`, borderRadius: 4, transition: "width 0.6s ease" }} />
            </div>
          </div> */}

          {/* // new code */}
          {/* Score ring */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <ScoreRing score={r.ats_score} animate={false} size={100} />
          </div>

          {/* Quick metrics row */}
          <div className="db-metrics-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
            {[
              { label: "ATS Score", value: r.ats_score, unit: "/100", color: scoreColor, bg: scoreBg },
              { label: "Keyword Match", value: `${r.match_rate}%`, unit: "match rate", color: "#2563eb", bg: "#eff6ff" },
              { label: "Missing Keywords", value: r.missing_count, unit: "gaps found", color: "#dc2626", bg: "#fef2f2" },
            ].map(m => (
              <div key={m.label} style={{ background: m.bg, borderRadius: 10, padding: "12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.07em", color: m.color, fontWeight: 600, marginBottom: 4, opacity: 0.8 }}>{m.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: m.color, lineHeight: 1 }}>{m.value}</div>
                <div style={{ fontSize: 10, color: m.color, marginTop: 3, opacity: 0.6 }}>{m.unit}</div>
              </div>
            ))}
          </div>

          {/* Panel 1: Summary */}
          {r.summary && (
            <CollapsiblePanel icon="📋" title="AI Summary" defaultOpen={true}>
              <p style={{ fontSize: 13, color: "#1e40af", lineHeight: 1.7, margin: 0, padding: "2px 0" }}>{r.summary}</p>
            </CollapsiblePanel>
          )}

          {/* Panel 2: Keywords */}
          {(r.present_keywords.length > 0 || r.missing_keywords.length > 0) && (
            <CollapsiblePanel
              icon="🔑"
              title="Keywords"
              badge={`${r.present_keywords.length} found · ${r.missing_keywords.length} missing`}
              badgeBg="#f1f5f9"
              badgeColor="#475569"
              defaultOpen={true}
            >
              <div className="db-kw-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                    ✅ Found ({r.present_keywords.length})
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {r.present_keywords.length > 0
                      ? r.present_keywords.map(k => (
                          <span key={k} style={{ background: "#d1fae5", color: "#065f46", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>{k}</span>
                        ))
                      : <span style={{ fontSize: 12, color: "#94a3b8" }}>None detected</span>}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                    ❌ Missing ({r.missing_keywords.length})
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {r.missing_keywords.length > 0
                      ? r.missing_keywords.map(k => (
                          <span key={k} style={{ background: "#fee2e2", color: "#dc2626", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500 }}>{k}</span>
                        ))
                      : <span style={{ fontSize: 12, color: "#94a3b8" }}>No major gaps</span>}
                  </div>
                </div>
              </div>
            </CollapsiblePanel>
          )}

          {/* Panel 3: Section Feedback */}
          {r.sections.length > 0 && (
            <CollapsiblePanel
              icon="📑"
              title="Section Feedback"
              badge={`${r.sections.length} sections`}
              badgeBg="#f1f5f9"
              badgeColor="#475569"
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                {r.sections.map((s, i) => {
                  const c = s.score === "Good" ? "#059669" : s.score === "Fair" ? "#d97706" : "#dc2626";
                  const b = s.score === "Good" ? "#ecfdf5" : s.score === "Fair" ? "#fffbeb" : "#fef2f2";
                  return (
                    <div key={i} style={{ padding: "12px 0", borderBottom: i < r.sections.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{s.name}</span>
                        <span style={{ background: b, color: c, fontSize: 10, padding: "2px 10px", borderRadius: 20, fontWeight: 700, letterSpacing: "0.04em" }}>{s.score}</span>
                      </div>
                      <p style={{ fontSize: 12, color: "#475569", margin: 0, lineHeight: 1.6 }}>{s.feedback}</p>
                    </div>
                  );
                })}
              </div>
            </CollapsiblePanel>
          )}

          {/* Panel 4: Suggested Rewrites */}
          {r.rewrites.length > 0 && (
            <CollapsiblePanel
              icon="✏️"
              title="Suggested Rewrites"
              badge={`${r.rewrites.length} suggestions`}
              badgeBg="#eff6ff"
              badgeColor="#2563eb"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {r.rewrites.map((rw, i) => (
                  <div key={i} style={{ paddingBottom: i < r.rewrites.length - 1 ? 16 : 0, borderBottom: i < r.rewrites.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                    {rw.section && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{rw.section}</div>
                    )}
                    <div style={{ fontSize: 12, color: "#475569", padding: "10px 14px", background: "#f8fafc", borderRadius: 8, marginBottom: 6, border: "1px solid #e2e8f0", lineHeight: 1.6 }}>{rw.before}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", margin: "6px 0" }}>↓ improved</div>
                    <div style={{ fontSize: 12, color: "#2563eb", padding: "10px 14px", background: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe", lineHeight: 1.6 }}>{rw.after}</div>
                  </div>
                ))}
              </div>
            </CollapsiblePanel>
          )}

          {/* Panel 5: Top Recommendations */}
          {r.top_recommendations.length > 0 && (
            <CollapsiblePanel
              icon="💡"
              title="Top Recommendations"
              badge={`${r.top_recommendations.length} items`}
              badgeBg="#fffbeb"
              badgeColor="#d97706"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {r.top_recommendations.map((rec, i) => (
                  <div key={i} style={{ padding: "10px 14px", background: "#fffbeb", borderRadius: 8, fontSize: 12, display: "flex", gap: 10, alignItems: "flex-start", border: "1px solid #fde68a" }}>
                    <span style={{ color: "#d97706", fontWeight: 800, minWidth: 20, fontSize: 13 }}>{i + 1}.</span>
                    <span style={{ color: "#334155", lineHeight: 1.6 }}>{rec}</span>
                  </div>
                ))}
              </div>
            </CollapsiblePanel>
          )}

          {/* Download footer */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16, paddingTop: 16, borderTop: "1px solid #e2e8f0" }}>
            <button
              onClick={handleDownload}
              style={{ padding: "10px 28px", background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", border: "none", borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.25)", transition: "all 0.15s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(37,99,235,0.35)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(37,99,235,0.25)"; }}
            >
              ↓ Download Full Report
            </button>
          </div>

        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user, userData, logout } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("loading");
  const [showPayModal, setShowPayModal] = useState(false);
  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("ats_basic");
  const [paymentId, setPaymentId] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [bannerType, setBannerType] = useState(null);
  const [hasPendingTransaction, setHasPendingTransaction] = useState(false);

  const [resumeText, setResumeText] = useState("");
  const [resumeFileName, setResumeFileName] = useState("");
  const [jdText, setJdText] = useState("");
  const [uploadMode, setUploadMode] = useState("upload");
  const fileInputRef = useRef();

  const [result, setResult] = useState(null);
  const [allAnalyses, setAllAnalyses] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [hasPreviousAnalyses, setHasPreviousAnalyses] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [error, setError] = useState("");
  const [txUsage, setTxUsage] = useState(null);
  // eslint-disable-next-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  const [savedResumeText, setSavedResumeText] = useState("");
  const [showAnalysisModal, setShowAnalysisModal] = useState(false); // analysis+template select modal
  const [txAnalyses, setTxAnalyses] = useState([]);  // analyses for current transaction
  const [inputLimits, setInputLimits] = useState({ maxResumeLength: 15000, maxJdLength: 10000 }); // from server /config
  // Server plan prices — always matches what Stripe actually charges
//  const [serverPlans, setServerPlans] = useState({});

  const [mismatchWarning, setMismatchWarning] = useState(null); // { resumeField, jdField }
  const [mismatchDismissed, setMismatchDismissed] = useState(false);

  // ── Fetch config from server — prices and input limits ──
  useEffect(() => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "https://ats-app-795v.onrender.com";
    fetch(`${BACKEND}/config`)
      .then(r => r.json())
      .then(d => {
        if (d.maxResumeLength && d.maxJdLength) setInputLimits(d);
        //if (d.plans) setServerPlans(d.plans); // store server-side prices
      })
      .catch(() => {}); // silently use defaults if fetch fails
  }, []);

  const firstName  = userData?.firstName || user?.displayName?.split(" ")[0] || "there";
  // eslint-disable-next-line no-unused-vars
  const atsPlan      = servicesConfig.plans.find(p => p.id === "ats_basic");
  // Live price from server — always matches what Stripe charges
  //const serverAtsPrice = serverPlans?.ats_basic?.priceDisplay || null;
  //const atsPriceDisplay = serverAtsPrice || atsPlan?.price || "$2.99";
  const { fullDisplay: atsPriceDisplay } = usePricing("ats_basic");
  // ── Can user pay for a new plan? Only when current transaction is fully exhausted ──
  const canPayForNewPlan = !txUsage || txUsage.resumeGenerated;
  const fullName = `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim() || "User";

  useEffect(() => {
    async function checkTransactionState() {
      if (!user) return;
      try {
        await user.reload();

        // ── Load all transactions ──
        const snapshot = await getDocs(collection(db, "users", user.uid, "transactions"));
        const allTx = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          //.filter(t => t.status === "paid")
          .filter(t => t.status === "paid" && !t.refunded)
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

        if (allTx.length === 0) {
          setStep("intro");
          trackPageView(user.uid, "dashboard_intro").catch(() => {});
          return;
        }

        const mostRecent = allTx[0];
        const usage = await getTransactionUsage(user.uid, mostRecent.id);
        setTxUsage(usage);
        setTransactionId(mostRecent.id);
        setPaymentId(mostRecent.paymentIntentId);
        setSelectedPlanId(mostRecent.planId || "ats_basic");

        // ── Load analyses for this transaction ──
        const analyses = await getAllAnalyses(user.uid);
        const txAnalyses = analyses.filter(a => a.transactionId === mostRecent.id);
        setTxAnalyses(txAnalyses);
        if (analyses.length > 0) {
          setAllAnalyses(analyses);
          setHasPreviousAnalyses(true);
        }

        // ── CASE 1: Never analysed → upload for first analysis ──
        if (!mostRecent.analysed) {
          setBannerType("pending");
          setHasPendingTransaction(true);
          setStep("upload");
          return;
        }

        // ── CASE 2: Resume already generated → history ──
        if (mostRecent.resumeGenerated) {
          setAnalysisComplete(true);
          setHasPreviousAnalyses(true);
          if (txAnalyses.length > 0) setResult(safeResult(txAnalyses[0]));
          setStep("done");
          return;
        }

        // ── CASE 3: Has at least 1 analysis done → show results ──
        // User can re-analyze (if count allows) OR generate resume from results page
        if (txAnalyses.length > 0) {
          setResult(safeResult(txAnalyses[0])); // show most recent
        }
        setAnalysisComplete(true);
        setHasPendingTransaction(false);
        setBannerType(null);
        setStep("results");

      } catch (err) {
        console.error(err);
        setStep("intro");
      }
    }
    checkTransactionState();
  }, [user, setBannerType]);

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError("");
    try {
      const text = await extractFileText(file);
      if (!text || text.length < 50) { setError("Could not extract text. Try pasting instead."); return; }
      setResumeText(text);
      setResumeFileName(file.name);
    } catch (err) {
      setError(err.message || "Could not read file.");
    }
  }

  async function handleAnalyze() {
    setError("");
    if (!resumeText.trim()) { setError("Please provide your resume."); return; }
    if (!jdText.trim()) { setError("Please provide the job description."); return; }

    // ── Client-side limit checks (mirrors server) ──
    if (resumeText.length > inputLimits.maxResumeLength) {
      setError(`Resume is too long (${resumeText.length.toLocaleString()} chars). Max allowed: ${inputLimits.maxResumeLength.toLocaleString()} characters. Please shorten it.`);
      return;
    }
    if (jdText.length > inputLimits.maxJdLength) {
      setError(`Job description is too long (${jdText.length.toLocaleString()} chars). Max allowed: ${inputLimits.maxJdLength.toLocaleString()} characters. Please shorten it.`);
      return;
    }

    const mismatch = detectCareerMismatch(resumeText, jdText);
    if (mismatch && !mismatchDismissed) {
      setMismatchWarning(mismatch);
      return; // pause — user must dismiss before proceeding
    }

    setMismatchWarning(null);
    setAnalyzing(true);

    // ── Client-side word count guard (server also validates) ──
    const wordCount = resumeText.trim().split(/\s+/).length;
    if (wordCount < 50) {
      setError("Your resume seems too short. Please paste the full resume content (minimum 50 words).");
      return;
    }



  setMismatchWarning(null);
  setAnalyzing(true);
    setAnalyzing(true);
    setStep("analyzing");

    // ── Track analysis start ──
    await trackAnalysisStart(user.uid, transactionId).catch(() => {});

    try {
      const rawAnalysis = await analyzeResume(resumeText, jdText);
      const analysis = normalizeResult(rawAnalysis);

      // ── Validate AI returned usable data ──
      if (!analysis || typeof analysis.ats_score !== "number") {
        throw new Error("Analysis returned incomplete data. Please try again.");
      }

      setResult(analysis);

      // ── Save analysis to Firestore ──
      await addDoc(collection(db, "users", user.uid, "analyses"), {
        createdAt:         serverTimestamp(),
        paymentId,
        transactionId,
        planId:            selectedPlanId,
        resumeText:        resumeText,
        jobDescription:    jdText,
        ats_score:         analysis.ats_score,
        match_rate:        analysis.match_rate,
        missing_count:     analysis.missing_count,
        summary:           analysis.summary || "",
        present_keywords:  Array.isArray(analysis.present_keywords)  ? analysis.present_keywords  : [],
        missing_keywords:  Array.isArray(analysis.missing_keywords)  ? analysis.missing_keywords  : [],
        all_jd_keywords:   Array.isArray(analysis.all_jd_keywords)   ? analysis.all_jd_keywords   : [],
        sections:          Array.isArray(analysis.sections)           ? analysis.sections           : [],
        rewrites:          Array.isArray(analysis.rewrites)           ? analysis.rewrites           : [],
        top_recommendations: Array.isArray(analysis.top_recommendations) ? analysis.top_recommendations : [],
      });

      // ── markTransactionComplete handles: status update + trackEvent + incrementUserStat ──
      if (transactionId) await markTransactionComplete(user.uid, transactionId);

      // ── Persist resume text for resume generation step ──
      setSavedResumeText(resumeText);

      // ── Refresh usage stats + txAnalyses ──
      if (transactionId) {
        const usage   = await getTransactionUsage(user.uid, transactionId);
        setTxUsage(usage);
        const allA    = await getAllAnalyses(user.uid);
        const txA     = allA.filter(a => a.transactionId === transactionId);
        setTxAnalyses(txA);
        setAllAnalyses(allA);
      }

      setAnalysisComplete(true);
      setHasPreviousAnalyses(true);
      setHasPendingTransaction(false);
      setBannerType(null);
      setStep("results");

    } catch (err) {
      console.error(err);
      await trackAnalysisError(user.uid, transactionId, err.message).catch(() => {});
      setError("Analysis failed: " + (err.message || "Please try again."));
      setStep("upload");
    }

    setAnalyzing(false);
  }

  // ── Service selection → payment flow ──
  function handleGetStarted(planId = "ats_basic") {
    // Block payment if user still has active transaction
    if (txUsage && !txUsage.resumeGenerated) {
      if (txUsage.canAnalyzeAgain) {
        setResumeText(""); setResumeFileName(""); setJdText("");
        setResult(null); setAnalysisComplete(false); setError("");
        setBannerType("pending"); setStep("upload");
        return;
      }
      if (txUsage.canGenerateResume) {
        setStep("results");
        return;
      }
    }
    // OK to pay — open payment modal
    setSelectedPlanId(planId);
    setShowServiceSelector(true);
  }

  function handleServiceSelected(planId) {
    setSelectedPlanId(planId);
    setShowServiceSelector(false);
    setShowPayModal(true);
  }

  async function handlePaymentSuccess(id, includes, paymentMeta = {}) {
    try {
      const txId = await saveTransaction(user.uid, id, selectedPlanId, includes, paymentMeta);

      // ── Load fresh usage for the NEW transaction ──
      const usage = await getTransactionUsage(user.uid, txId);

      // ── Fully reset all state for new payment cycle ──
      setPaymentId(id);
      setTransactionId(txId);
      setSelectedPlanId(selectedPlanId);
      setTxUsage(usage);
      setTxAnalyses([]);          // clear old transaction's analyses
      setHasPendingTransaction(true);
      setShowPayModal(false);
      setBannerType("pending");
      setResumeText("");
      setResumeFileName("");
      setJdText("");
      setResult(null);
      setAllAnalyses([]);
      setAnalysisComplete(false);
      setAnalyzing(false);
      setError("");
      setSavedResumeText("");
      setStep("upload");
      // Add to both reset flows:
      setMismatchWarning(null);
      setMismatchDismissed(false);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleNewAnalysis() {
    setShowUserMenu(false);
    setError("");

    try {
      // ── Priority 1: Current transaction has resume generation pending ──
      if (txUsage?.canGenerateResume && !txUsage?.resumeGenerated) {
        setStep("results");
        return;
      }

      // ── Priority 2: Current transaction has analyses remaining ──
      if (txUsage?.canAnalyzeAgain && transactionId) {
        setResumeText(""); setResumeFileName(""); setJdText("");
        setResult(null); setAnalysisComplete(false);
        setBannerType("pending");
        setHasPendingTransaction(true);
        setStep("upload");
        return;
      }

      // ── Priority 3: A brand-new unpaid/unanalysed transaction exists ──
      const pending = await getPendingTransaction(user.uid);
      if (pending) {
        setPaymentId(pending.paymentIntentId);
        setTransactionId(pending.id);
        setBannerType("pending");
        setHasPendingTransaction(true);
        const usage = await getTransactionUsage(user.uid, pending.id);
        setTxUsage(usage);
        setResumeText(""); setResumeFileName(""); setJdText("");
        setResult(null); setAnalysisComplete(false);
        setStep("upload");
        return;
      }

      // ── Priority 4: Everything used — go to intro to buy new plan ──
      setStep("intro");
      setAllAnalyses([]);
      setExpandedId(null);
      setPaymentId(null);
      setTransactionId(null);
      setTxUsage(null);
      setBannerType(null);
      setHasPendingTransaction(false);
      setResumeText(""); setResumeFileName(""); setJdText("");
      setResult(null); setAnalysisComplete(false);
      // Add to both reset flows:
      setMismatchWarning(null);
      setMismatchDismissed(false);

    } catch (err) {
      console.error(err);
      setStep("intro");
    }
  }

  async function handleGoToDashboard() {
    setShowUserMenu(false);
    const analyses = await getAllAnalyses(user.uid);
    setAllAnalyses(analyses);
    setHasPreviousAnalyses(true);
    setStep("done");
  }

  // ── Navigate to results — always loads latest txAnalysis into result state ──
  async function handleGoToResults() {
    setShowUserMenu(false);
    // Reload txAnalyses if empty
    let currentTxAnalyses = txAnalyses;
    if (currentTxAnalyses.length === 0 && transactionId) {
      const allA = await getAllAnalyses(user.uid);
      currentTxAnalyses = allA.filter(a => a.transactionId === transactionId);
      setTxAnalyses(currentTxAnalyses);
      setAllAnalyses(allA);
    }
    // Load best analysis (highest score) into result
    if (currentTxAnalyses.length > 0) {
      const best = [...currentTxAnalyses].sort((a, b) => (b.ats_score || 0) - (a.ats_score || 0))[0];
      setResult(safeResult(best));
    }
    setAnalysisComplete(true);
    setStep("results");
  }

  async function handleLogout() {
    await logout();
    navigate("/");
  }

  function handleDownload() {
    if (!result) return;
    generateAndDownloadReport(safeResult(result), fullName);
    trackReportDownload(user.uid, null).catch(() => {});
  }

  // ── Handle service card click from intro ──
  // eslint-disable-next-line no-unused-vars
  function handleServiceCardClick(svc) {
    if (svc.comingSoon) return;
    trackServiceClick(user.uid, svc.planId || svc.serviceCode).catch(() => {});
    if (!svc.planId) {
      if (svc.serviceCode === "RESUME_GENERATOR") navigate("/generate-resume");
      return;
    }
    // Reset stale form state before opening payment
    setResumeText("");
    setResumeFileName("");
    setJdText("");
    setResult(null);
    setAnalysisComplete(false);
    setAnalyzing(false);
    setError("");
    handleServiceSelected(svc.planId);
  }

  if (step === "loading") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#2563eb", animation: "spin 0.7s linear infinite", margin: "0 auto 16px" }} />
          <p style={{ fontSize: 14, color: "#64748b" }}>Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column", fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* ── NAV ── */}
      <nav style={{ borderBottom: "1px solid #e2e8f0", padding: "0 5%", height: 70, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ffffff", position: "sticky", top: 0, zIndex: 1000, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }} onClick={handleGoToDashboard}>
          <div style={{ width: 36, height: 36, background: "linear-gradient(135deg, #2563eb, #1d4ed8)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(37,99,235,0.2)" }}>
            <span style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}>R</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 20, color: "#1e293b" }}>
            ResumeScore<span style={{ color: "#2563eb" }}>AI</span>
          </span>
        </div>

        {/* User menu */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setShowUserMenu(prev => !prev)}
            className="db-nav-btn" style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "1px solid #e2e8f0", borderRadius: 10, padding: "6px 14px", cursor: "pointer", fontSize: 14, color: "#334155" }}
            onMouseEnter={e => e.currentTarget.style.borderColor = "#2563eb"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "#e2e8f0"}
          >
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {firstName.charAt(0).toUpperCase()}
            </div>
            <span className="db-nav-name" style={{ fontWeight: 500 }}>{firstName}</span>
            <span style={{ fontSize: 10, color: "#94a3b8" }}>{showUserMenu ? "▲" : "▼"}</span>
          </button>

          {showUserMenu && (
            <>
              {/* ── Backdrop — zIndex must be BELOW the menu ── */}
              <div
                style={{ position: "fixed", inset: 0, zIndex: 998 }}
                onClick={() => setShowUserMenu(false)}
              />

              {/* ── Menu — sits above backdrop, below modals ── */}
              <div style={{
                position: "fixed",
                top: 70,
                right: 12,
                left: "auto",
                bottom: 12,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
                width: "min(300px, calc(100vw - 24px))",
                zIndex: 999,
                overflow: "auto",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
              }}>

                {/* ── User info + plan status ── */}
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {firstName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{userData?.firstName} {userData?.lastName}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{userData?.email || user?.email}</div>
                    </div>
                  </div>

                  {/* ── Active plan status pill ── */}
                  {txUsage && !txUsage.resumeGenerated ? (
                    <div style={{ background: "#fff", border: "1px solid #bfdbfe", borderRadius: 9, padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#1e40af" }}>{getPlanName(txUsage.planId || "ats_basic")}</div>
                        <div style={{ fontSize: 10, background: "#dbeafe", color: "#1d4ed8", padding: "1px 7px", borderRadius: 20, fontWeight: 700 }}>Active</div>
                      </div>
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        {Array.from({ length: txUsage.analysisCount }).map((_, i) => (
                          <div key={i} style={{ width: 18, height: 18, borderRadius: "50%", background: i < txUsage.analysisUsedCount ? "#2563eb" : "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800, color: i < txUsage.analysisUsedCount ? "#fff" : "#94a3b8" }}>
                            {i < txUsage.analysisUsedCount ? "✓" : i + 1}
                          </div>
                        ))}
                        <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: 2 }}>analyses</span>
                        <span style={{ fontSize: 10, color: "#94a3b8", margin: "0 2px" }}>·</span>
                        <span style={{ fontSize: 10, color: txUsage.canGenerateResume ? "#059669" : "#94a3b8", fontWeight: 600 }}>
                          {txUsage.canGenerateResume ? "✨ Resume ready" : ""}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "#94a3b8", background: "#f8fafc", borderRadius: 7, padding: "6px 10px" }}>
                      {canPayForNewPlan ? "No active plan" : "All done"}
                    </div>
                  )}
                </div>

                {/* ── PRIMARY ACTIONS ── */}
                <div style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ padding: "6px 20px 4px" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Actions</span>
                  </div>

                  {/* Resume Analysis */}
                  <button
                    onClick={() => { setShowUserMenu(false); handleNewAnalysis(); }}
                    disabled={!canPayForNewPlan && !txUsage?.canAnalyzeAgain && !txUsage?.canGenerateResume}
                    style={{ width: "100%", padding: "11px 20px", background: "none", border: "none", textAlign: "left", fontSize: 14, cursor: (!canPayForNewPlan && !txUsage?.canAnalyzeAgain && !txUsage?.canGenerateResume) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 12, opacity: (!canPayForNewPlan && !txUsage?.canAnalyzeAgain && !txUsage?.canGenerateResume) ? 0.4 : 1 }}
                    onMouseEnter={e => { if (canPayForNewPlan || txUsage?.canAnalyzeAgain || txUsage?.canGenerateResume) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <IconBox name="target" size={32} color={txUsage?.canAnalyzeAgain?"#2563eb":"#94a3b8"} bg={txUsage?.canAnalyzeAgain?"#eff6ff":"#f8fafc"} radius={8} strokeWidth={1.7} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>Resume Analysis</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                        {txUsage?.canAnalyzeAgain
                          ? `${txUsage.analysisRemaining} of ${txUsage.analysisCount} left`
                          : canPayForNewPlan ? `Buy a plan to start — ${atsPriceDisplay}`
                          : "All analyses used"}
                      </div>
                    </div>
                    {txUsage?.canAnalyzeAgain && (
                      <span style={{ fontSize: 11, background: "#eff6ff", color: "#2563eb", padding: "2px 8px", borderRadius: 20, fontWeight: 700, flexShrink: 0 }}>
                        {txUsage.analysisRemaining} left
                      </span>
                    )}
                  </button>

                  {/* Generate Resume */}
                  <button
                    onClick={() => { setShowUserMenu(false); if (txUsage?.canGenerateResume) { handleGoToResults(); } else { navigate("/generate-resume"); } }}
                    style={{ width: "100%", padding: "11px 20px", background: "none", border: "none", textAlign: "left", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <IconBox name="fileText" size={32} color={txUsage?.canGenerateResume?"#059669":"#94a3b8"} bg={txUsage?.canGenerateResume?"#f0fdf4":"#f8fafc"} radius={8} strokeWidth={1.7} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>Generate Resume</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                        {txUsage?.canGenerateResume ? "Ready — pick a template" : "Unlocks after first analysis"}
                      </div>
                    </div>
                    {txUsage?.canGenerateResume && (
                      <span style={{ fontSize: 11, background: "#f0fdf4", color: "#059669", padding: "2px 8px", borderRadius: 20, fontWeight: 700, flexShrink: 0 }}>Ready</span>
                    )}
                  </button>

                  {/* generate resume from scratch */}
                  <button
                    onClick={() => { setShowUserMenu(false); navigate("/build-resume"); }}
                    style={{ width: "100%", padding: "11px 20px", background: "none", border: "none", textAlign: "left", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <IconBox name="edit" size={32} color="#7c3aed" bg="#faf5ff" radius={8} strokeWidth={1.7} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>Free Resume Builder</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>No resume? Build one from scratch</div>
                    </div>
                    <span style={{ fontSize: 11, background: "#EAF3DE", color: "#3B6D11", padding: "2px 8px", borderRadius: 20, fontWeight: 700, flexShrink: 0 }}>FREE</span>
                  </button>

                 {/* Cover Letter */}
                  <button
                    onClick={() => { setShowUserMenu(false); navigate("/cover-letter"); }}
                    disabled={!txUsage?.coverLetterRemaining}
                    style={{ width: "100%", padding: "11px 20px", background: "none", border: "none", textAlign: "left", fontSize: 14, cursor: txUsage?.coverLetterRemaining > 0 ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 12, opacity: txUsage?.coverLetterRemaining > 0 ? 1 : 0.4 }}
                    onMouseEnter={e => { if (txUsage?.coverLetterRemaining > 0) e.currentTarget.style.background = "#f8fafc"; }}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <IconBox name="mail" size={32} color={txUsage?.coverLetterRemaining > 0 ? "#0891b2" : "#94a3b8"} bg={txUsage?.coverLetterRemaining > 0 ? "#f0f9ff" : "#f8fafc"} radius={8} strokeWidth={1.7} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>Cover Letter</div>
                      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                        {txUsage?.coverLetterRemaining > 0 ? `${txUsage.coverLetterRemaining} remaining` : "Unlocks with plan"}
                      </div>
                    </div>
                    {txUsage?.coverLetterRemaining > 0 && (
                      <span style={{ fontSize: 11, background: "#f0f9ff", color: "#0891b2", padding: "2px 8px", borderRadius: 20, fontWeight: 700, flexShrink: 0 }}>
                        {txUsage.coverLetterRemaining} left
                      </span>
                    )}
                  </button>

                </div>

                {/* ── NAVIGATION ── */}
                <div style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ padding: "6px 20px 4px" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Account</span>
                  </div>

                  {[
                    { icon: "barChart", label: "Analysis History", sub: allAnalyses.length > 0 ? `${allAnalyses.length} saved` : "None yet", onClick: () => { setShowUserMenu(false); handleGoToDashboard(); } },
                    { icon: "user", label: "My Account",        sub: "History, payments & usage",    onClick: () => { setShowUserMenu(false); navigate("/account"); }       },
                    { icon: "settings", label: "Profile",           sub: "Update your details",          onClick: () => { setShowUserMenu(false); navigate("/profile"); }       },
                    { icon: "help", label: "Support",           sub: "Questions or issues?",         onClick: () => { setShowUserMenu(false); navigate("/support"); }       },
                  ].map(item => (
                    <button key={item.label} onClick={item.onClick}
                      style={{ width: "100%", padding: "10px 20px", background: "none", border: "none", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}
                    >
                      <IconBox name={item.icon} size={30} color="#475569" radius={7} strokeWidth={1.7} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13, color: "#0f172a" }}>{item.label}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{item.sub}</div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* ── Admin — only for admin emails ── */}
                {["arsh1916@gmail.com", "support@smartaicareer.com"].includes(user?.email) && (
                  <div style={{ padding: "4px 0", borderBottom: "1px solid #f1f5f9" }}>
                    <button
                      onClick={() => { setShowUserMenu(false); navigate("/admin"); }}
                      style={{ width: "100%", padding: "10px 20px", background: "none", border: "none", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                      onMouseEnter={e => e.currentTarget.style.background = "#faf5ff"}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}
                    >
                      <IconBox name="settings" size={30} color="#7c3aed" bg="#faf5ff" radius={7} strokeWidth={1.7} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13, color: "#7c3aed" }}>Admin Dashboard</div>
                        <div style={{ fontSize: 11, color: "#a78bfa", marginTop: 1 }}>Users & transactions</div>
                      </div>
                    </button>
                  </div>
                )}

                {/* ── Sign out ── */}
                <div style={{ padding: "4px 0" }}>
                  <button
                    onClick={() => { setShowUserMenu(false); handleLogout(); }}
                    style={{ width: "100%", padding: "10px 20px", background: "none", border: "none", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#fef2f2"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}
                  >
                    <IconBox name="logOut" size={30} color="#dc2626" bg="#fef2f2" radius={7} strokeWidth={1.7} />
                    <div style={{ fontWeight: 500, fontSize: 13, color: "#dc2626" }}>Sign Out</div>
                  </button>
                </div>

              </div>{/* end menu */}
            </>
          )}
        </div>
      </nav>

      <div className="db-main" style={{ flex: 1, maxWidth: 1400, margin: "0 auto", padding: "32px 3%", width: "100%" }}>

        {/* ── INTRO ── */}
        {step === "intro" && (
          <>
            {/* ── Welcome header ── */}
            <div style={{ marginBottom: 24, textAlign: "center" }}>
              <h1 className="db-intro-title" style={{ fontSize: "clamp(26px,4vw,34px)", fontWeight: 800, color: "#0f172a", marginBottom: 10, letterSpacing: "-0.02em" }}>
                Welcome back, {firstName}! 👋
              </h1>
              <p style={{ fontSize: 15, color: "#64748b", maxWidth: 440, margin: "0 auto" }}>
                {txUsage && !txUsage.resumeGenerated
                  ? "You have an active plan — continue where you left off."
                  : "Choose a plan below to get started. Each plan is a one-time payment."}
              </p>
            </div>

            {/* ── Active transaction banner ── */}
            {txUsage && !txUsage.resumeGenerated && (
              <div style={{ background: "linear-gradient(135deg,#f0fdf4,#dcfce7)", border: "1.5px solid #86efac", borderRadius: 16, padding: "18px 22px", marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
                    {txUsage.canAnalyzeAgain ? "🔄" : "✨"}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#166534", marginBottom: 3 }}>
                      {txUsage.canAnalyzeAgain
                        ? `You have ${txUsage.analysisRemaining} ${txUsage.analysisRemaining === 1 ? "analysis" : "analyses"} remaining`
                        : "Your resume generation is ready!"}
                    </div>
                    <div style={{ fontSize: 13, color: "#15803d" }}>
                      {txUsage.canAnalyzeAgain
                        ? `${txUsage.analysisUsedCount} of ${txUsage.analysisCount} analyses used · Resume generation unlocked after analysis`
                        : `All ${txUsage.analysisCount} analyses complete · Select your best analysis and generate your improved resume`}
                    </div>
                    {/* Progress dots */}
                    <div style={{ display: "flex", gap: 5, marginTop: 8, alignItems: "center" }}>
                      {Array.from({ length: txUsage.analysisCount }).map((_, i) => (
                        <div key={i} style={{ width: 22, height: 22, borderRadius: "50%", background: i < txUsage.analysisUsedCount ? "#059669" : "#d1fae5", border: `2px solid ${i < txUsage.analysisUsedCount ? "#059669" : "#86efac"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: i < txUsage.analysisUsedCount ? "#fff" : "#6ee7b7" }}>
                          {i < txUsage.analysisUsedCount ? "✓" : i + 1}
                        </div>
                      ))}
                      <span style={{ fontSize: 10, color: "#86efac", margin: "0 2px" }}>→</span>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: txUsage.canGenerateResume ? "#059669" : "#d1fae5", border: `2px solid ${txUsage.canGenerateResume ? "#059669" : "#86efac"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                        {txUsage.canGenerateResume ? "✨" : "🔒"}
                      </div>
                      <span style={{ fontSize: 11, color: "#15803d", marginLeft: 4 }}>Resume</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {txUsage.canAnalyzeAgain && (
                    <button
                      onClick={() => { setResumeText(""); setResumeFileName(""); setJdText(""); setResult(null); setAnalysisComplete(false); setError(""); setBannerType("pending"); setStep("upload"); }}
                      style={{ padding: "10px 18px", background: "#fff", color: "#059669", border: "1.5px solid #059669", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0fdf4"}
                      onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                    >
                      🔄 Continue Analysis
                    </button>
                  )}
                  {txUsage.canGenerateResume && (
                    <button
                      onClick={handleGoToResults}
                      style={{ padding: "10px 18px", background: "linear-gradient(135deg,#059669,#047857)", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: "0 4px 12px rgba(5,150,105,0.3)" }}
                      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                      onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                    >
                      ✨ Generate My Resume →
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── PLANS GRID ── */}
            <div style={{ marginBottom: 40 }}>
              <div className="db-svc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
                {servicesConfig.plans.filter(p => p.enabled).map(plan => {
                  const isComingSoon = !plan.active;
                  const planServices = servicesConfig.services.filter(s => plan.includes?.includes(s.serviceCode));

                  return (
                    <div key={plan.id}
                      onClick={() => { if (!isComingSoon) { setSelectedPlanId(plan.id); setShowPayModal(true); } }}
                      style={{
                        background: "#fff",
                        borderRadius: 20,
                        border: plan.highlight ? "2px solid #2563eb" : "1.5px solid #e2e8f0",
                        overflow: "hidden",
                        cursor: isComingSoon ? "default" : "pointer",
                        opacity: isComingSoon ? 0.8 : 1,
                        transition: "all 0.2s ease",
                        display: "flex", flexDirection: "column",
                        boxShadow: plan.highlight ? "0 8px 28px rgba(37,99,235,0.12)" : "0 2px 8px rgba(0,0,0,0.04)",
                      }}
                      onMouseEnter={e => { if (!isComingSoon) { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = plan.highlight ? "0 16px 40px rgba(37,99,235,0.18)" : "0 8px 24px rgba(0,0,0,0.1)"; } }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = plan.highlight ? "0 8px 28px rgba(37,99,235,0.12)" : "0 2px 8px rgba(0,0,0,0.04)"; }}
                    >
                      {/* Card header */}
                      <div style={{ padding: "22px 22px 16px", background: plan.highlight ? "linear-gradient(135deg,#eff6ff,#f0f9ff)" : "#fafaf9", borderBottom: "1px solid #f1f5f9" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>{plan.name}</div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>{plan.description}</div>
                          </div>
                          {isComingSoon ? (
                            <span style={{ fontSize: 10, background: "#f1f5f9", color: "#64748b", padding: "3px 10px", borderRadius: 20, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>Coming Soon</span>
                          ) : (
                            <span style={{ fontSize: 10, background: "#dcfce7", color: "#166534", padding: "3px 10px", borderRadius: 20, fontWeight: 700, border: "1px solid #bbf7d0", flexShrink: 0, marginLeft: 8 }}>● Available</span>
                          )}
                        </div>
                        {/* Price */}
                        {/* <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                          <span style={{ fontSize: 36, fontWeight: 900, color: plan.highlight ? "#2563eb" : "#0f172a", letterSpacing: "-0.03em", lineHeight: 1 }}>{plan.price}</span>
                          <span style={{ fontSize: 13, color: "#94a3b8" }}>{plan.period}</span>
                        </div> */}
                        {/* // new code */}
                        <PlanPrice planId={plan.id} />
                      </div>

                      {/* What's included */}
                      <div style={{ padding: "16px 22px", flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>What's included</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                          {planServices.map(svc => (
                            <div key={svc.serviceCode} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                              <div style={{ width: 32, height: 32, borderRadius: 9, background: svc.isFree ? "#f0fdf4" : "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                                {svc.icon}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{svc.title}</span>
                                  {svc.isFree && (
                                    <span style={{ fontSize: 10, background: "#dcfce7", color: "#166534", padding: "1px 7px", borderRadius: 10, fontWeight: 700 }}>FREE</span>
                                  )}
                                </div>
                                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2, lineHeight: 1.5 }}>{svc.description}</div>
                                {/* Highlights */}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                                  {svc.highlights.map(h => (
                                    <span key={h} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 20, background: "#f1f5f9", color: "#475569", fontWeight: 500 }}>{h}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* CTA button */}
                      <div style={{ padding: "16px 22px 20px" }}>
                        <button
                          onClick={e => { e.stopPropagation(); if (!isComingSoon) { setSelectedPlanId(plan.id); setShowPayModal(true); } else { navigate("/support"); } }}
                          style={{
                            width: "100%", padding: "13px",
                            background: isComingSoon ? "#f1f5f9" : plan.highlight ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : "linear-gradient(135deg,#0f172a,#1e293b)",
                            color: isComingSoon ? "#64748b" : "#fff",
                            border: "none", borderRadius: 12,
                            fontWeight: 700, fontSize: 14,
                            cursor: isComingSoon ? "default" : "pointer",
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {isComingSoon ? "🔔 Notify Me When Available" : `Get ${plan.name} — ${plan.price} →`}
                        </button>
                        {!isComingSoon && (
                          <p style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", marginTop: 8, marginBottom: 0 }}>
                            Secure Stripe payment · No subscription
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Quick links if user has history ── */}
            {(hasPreviousAnalyses || hasPendingTransaction) && (
              <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", padding: "20px 24px", marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                    {hasPendingTransaction ? "⏳ You have a pending analysis" : "📊 Continue where you left off"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {hasPendingTransaction ? "Upload your resume to use your paid analysis" : `${allAnalyses.length} previous ${allAnalyses.length === 1 ? "analysis" : "analyses"} available`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  {hasPendingTransaction && (
                    <button onClick={() => setStep("upload")}
                      style={{ padding: "9px 18px", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                      Start Analysis →
                    </button>
                  )}
                  {hasPreviousAnalyses && (
                    <button onClick={handleGoToDashboard}
                      style={{ padding: "9px 18px", background: "#f8fafc", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                      View History
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── HOW IT WORKS ── */}
            <div style={{ background: "#fff", borderRadius: 18, padding: "24px 28px", border: "1px solid #e2e8f0", marginBottom: 24 }}>
              <h2 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 20, letterSpacing: "-0.01em" }}>
                How it works
              </h2>
              <div className="db-steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 16 }}>
                {STEPS.map((s, i) => (
                  <div key={s.num} style={{ textAlign: "center", position: "relative" }}>
                    {i < STEPS.length - 1 && (
                      <div style={{ position: "absolute", right: -8, top: 14, width: 16, height: 2, background: "#e2e8f0", display: "block" }} />
                    )}
                    <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg,#eff6ff,#dbeafe)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: 20, border: "2px solid #bfdbfe" }}>
                      {s.icon}
                    </div>
                    <div style={{ width: 20, height: 20, borderRadius: "50%", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", margin: "-14px auto 8px", position: "relative", zIndex: 1, boxShadow: "0 2px 6px rgba(37,99,235,0.3)" }}>{s.num}</div>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 3, color: "#0f172a" }}>{s.label}</div>
                    <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.5 }}>{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <p style={{ fontSize: 12, color: "#94a3b8" }}>🔒 Secure payment via Stripe · No subscription · Cancel anytime</p>
            </div>
          </>
        )}

        {/* ── DONE / HISTORY ── */}
        {step === "done" && (
          <>
            <div style={{ marginBottom: 32 }}>
              <h1 style={{ fontSize: "clamp(28px, 4vw, 32px)", fontWeight: 800, color: "#1e293b", marginBottom: 8, letterSpacing: "-0.02em" }}>
                Your Analysis History
              </h1>
              <p style={{ fontSize: 15, color: "#64748b" }}>View and download your past resume analyses.</p>
            </div>

            <div className="db-history-banner" style={{ background: canPayForNewPlan ? "linear-gradient(135deg,#ecfdf5,#d1fae5)" : "linear-gradient(135deg,#eff6ff,#dbeafe)", border: `1px solid ${canPayForNewPlan ? "#a7f3d0" : "#bfdbfe"}`, borderRadius: 16, padding: "20px 24px", marginBottom: 32, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              {/* Status */}
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontSize: 26 }}>{canPayForNewPlan ? "✅" : txUsage?.canAnalyzeAgain ? "🔄" : "✨"}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: canPayForNewPlan ? "#065f46" : "#1e40af" }}>
                    {canPayForNewPlan ? "All done — ready for a new plan"
                      : txUsage?.canAnalyzeAgain && txUsage?.canGenerateResume ? "Analysis in progress"
                      : txUsage?.canAnalyzeAgain ? `${txUsage.analysisRemaining} ${txUsage.analysisRemaining === 1 ? "analysis" : "analyses"} remaining`
                      : "Resume generation ready"}
                  </div>
                  <div style={{ fontSize: 12, color: canPayForNewPlan ? "#059669" : "#3b82f6", marginTop: 2 }}>
                    {canPayForNewPlan
                      ? `${allAnalyses.length} ${allAnalyses.length !== 1 ? "analyses" : "analysis"} saved`
                      : txUsage?.canAnalyzeAgain
                      ? `${txUsage.analysisUsedCount}/${txUsage.analysisCount} analyses used · Continue or generate resume`
                      : `${txUsage?.analysisCount || 0} analyses complete · Select best to generate resume`}
                  </div>
                </div>
              </div>

              {/* Action buttons — both shown when applicable */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {txUsage?.canAnalyzeAgain && (
                  <button
                    onClick={() => { setResumeText(""); setResumeFileName(""); setJdText(""); setResult(null); setAnalysisComplete(false); setError(""); setBannerType("pending"); setStep("upload"); }}
                    style={{ padding: "10px 16px", background: "#fff", color: "#2563eb", border: "1.5px solid #2563eb", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                  >
                    🔄 Continue Analysis
                    <span style={{ background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 800, padding: "1px 7px", borderRadius: 20 }}>
                      {txUsage.analysisRemaining} left
                    </span>
                  </button>
                )}
                {txUsage?.canGenerateResume && (
                  <button
                    onClick={handleGoToResults}
                    style={{ padding: "10px 16px", background: "linear-gradient(135deg,#059669,#047857)", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer", boxShadow: "0 4px 12px rgba(5,150,105,0.25)" }}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    ✨ Generate My Resume →
                  </button>
                )}
                {canPayForNewPlan && (
                  <button
                    onClick={() => handleGetStarted()}
                    style={{ padding: "10px 16px", background: "#fff", color: "#2563eb", border: "2px solid #2563eb", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#2563eb"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#2563eb"; }}
                  >
                    💳 {servicesConfig.app.ctaNavUser} — {atsPriceDisplay}
                  </button>
                )}
              </div>
            </div>

            {allAnalyses.length > 0 ? (
              allAnalyses.map((analysis, index) => (
                <HistoryCard
                  key={analysis.id}
                  analysis={analysis}
                  index={allAnalyses.length - index}
                  userName={fullName}
                  isExpanded={expandedId === analysis.id}
                  onExpand={() => setExpandedId(expandedId === analysis.id ? null : analysis.id)}
                />
              ))
            ) : (
              <div style={{ textAlign: "center", padding: "60px 20px", background: "#ffffff", borderRadius: 16, border: "1px solid #e2e8f0" }}>
                <p style={{ fontSize: 14, color: "#64748b", marginBottom: 24 }}>No analysis history found.</p>
                <button
                  onClick={handleGetStarted}
                  style={{ padding: "12px 32px", background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer" }}
                >
                  Start Your First Analysis
                </button>
              </div>
            )}
          </>
        )}

        {/* ── UPLOAD ── */}
        {step === "upload" && (
          <>
            {/* ── Page header ── */}
            <div style={{ marginBottom: 28 }}>
              <h1 style={{ fontSize: "clamp(22px,3.5vw,30px)", fontWeight: 800, color: "#0f172a", marginBottom: 8, letterSpacing: "-0.02em" }}>
                Analyze Your Resume
              </h1>
              <p style={{ fontSize: 14, color: "#64748b" }}>Upload your resume and paste the job description to get your ATS score.</p>
            </div>

            {/* ── Status banner ── */}
            {(() => {
              const analysisNum  = (txUsage?.analysisUsedCount || 0) + 1;
              const analysisMax  = txUsage?.analysisCount || 2;
              const isReAnalysis = analysisNum > 1;
              return (
                <div style={{ background: isReAnalysis ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : "linear-gradient(135deg,#ecfdf5,#d1fae5)", border: `1px solid ${isReAnalysis ? "#bfdbfe" : "#a7f3d0"}`, borderRadius: 14, padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{isReAnalysis ? "🔄" : "✅"}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: isReAnalysis ? "#1e40af" : "#065f46" }}>
                        {isReAnalysis ? `Re-analysis #${analysisNum} of ${analysisMax}` : "Payment confirmed — Ready for analysis"}
                      </div>
                      <div style={{ fontSize: 12, color: isReAnalysis ? "#3b82f6" : "#059669", marginTop: 2 }}>
                        {isReAnalysis
                          ? "Upload your resume and JD again to get a fresh score — or go back to your results"
                          : "Upload your resume and paste the job description below to get started"}
                      </div>
                    </div>
                  </div>
                  {/* Analysis counter pills */}
                  <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                    {Array.from({ length: analysisMax }).map((_, i) => (
                      <div key={i} style={{ width: 28, height: 28, borderRadius: "50%", background: i < txUsage?.analysisUsedCount ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : i === txUsage?.analysisUsedCount ? (isReAnalysis ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : "#eff6ff") : "#f1f5f9", border: `2px solid ${i < txUsage?.analysisUsedCount ? "#2563eb" : i === txUsage?.analysisUsedCount ? "#bfdbfe" : "#e2e8f0"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: i <= (txUsage?.analysisUsedCount || 0) ? (i < txUsage?.analysisUsedCount ? "#fff" : "#2563eb") : "#94a3b8" }}>
                        {i < (txUsage?.analysisUsedCount || 0) ? "✓" : i + 1}
                      </div>
                    ))}
                    <span style={{ fontSize: 11, color: "#64748b", marginLeft: 4 }}>analyses</span>
                  </div>
                </div>
              );
            })()}

            {error && (
              <div style={{ background: "#fef2f2", color: "#dc2626", padding: "12px 16px", borderRadius: 10, marginBottom: 20, fontSize: 13, border: "1px solid #fecaca", display: "flex", alignItems: "center", gap: 8 }}>
                <span>⚠️</span> {error}
              </div>
            )}

            {/* ── Step indicators ── */}
            <div className="db-step-bar" style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 24 }}>
              {[
                { num: "1", label: "Upload Resume", done: !!resumeText },
                { num: "2", label: "Add Job Description", done: !!jdText },
                { num: "3", label: "Get Your Score", done: false },
              ].map((step, i) => (
                <div key={step.num} style={{ display: "flex", alignItems: "center", flex: i < 2 ? 1 : "unset" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: step.done ? "#059669" : i === 0 && !resumeText ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : i === 1 && resumeText && !jdText ? "linear-gradient(135deg,#2563eb,#1d4ed8)" : step.done ? "#059669" : "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: step.done || i === 0 || (i === 1 && resumeText) ? "#fff" : "#94a3b8", flexShrink: 0, transition: "all 0.3s" }}>
                      {step.done ? "✓" : step.num}
                    </div>
                    <span className="db-step-bar-label" style={{ fontSize: 12, fontWeight: 600, color: step.done ? "#059669" : "#64748b", whiteSpace: "nowrap" }}>{step.label}</span>
                  </div>
                  {i < 2 && <div style={{ flex: 1, height: 2, background: step.done ? "#a7f3d0" : "#e2e8f0", margin: "0 10px", minWidth: 20, transition: "all 0.3s" }} />}
                </div>
              ))}
            </div>

            {/* ── RESUME CARD ── */}
            <div className="db-upload-card" style={{ background: "#ffffff", borderRadius: 20, marginBottom: 16, border: resumeText ? "2px solid #a7f3d0" : "1.5px solid #e2e8f0", transition: "border-color 0.3s", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>

              {/* Card header */}
              <div className="db-upload-card-header" style={{ padding: "18px 24px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, background: resumeText ? "linear-gradient(135deg,#f0fdf4,#ecfdf5)" : "#fafaf9" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: resumeText ? "#dcfce7" : "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name={resumeText?"checkCircle":"fileText"} size={18} color={resumeText?"#059669":"#2563eb"} strokeWidth={1.7} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Your Resume</div>
                    <div style={{ fontSize: 11, color: resumeText ? "#059669" : "#94a3b8" }}>
                      {resumeText ? `${resumeText.split(" ").length} words ready` : "Upload or paste your resume text"}
                    </div>
                  </div>
                </div>
                {/* Toggle */}
                <div className="db-upload-toggle" style={{ display: "flex", background: "#f1f5f9", borderRadius: 9, padding: 3, gap: 2 }}>
                  {[{ id: "upload", label: "Upload File" }, { id: "paste", label: "Paste Text" }].map(m => (
                    <button key={m.id} onClick={() => setUploadMode(m.id)}
                      style={{ padding: "6px 14px", borderRadius: 7, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", background: uploadMode === m.id ? "#fff" : "transparent", color: uploadMode === m.id ? "#1e293b" : "#64748b", boxShadow: uploadMode === m.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none", transition: "all 0.2s" }}
                    >{m.label}</button>
                  ))}
                </div>
              </div>

              {/* Card body */}
              <div style={{ padding: "20px 24px" }}>
                {uploadMode === "upload" ? (
                  <div
                    onClick={() => fileInputRef.current.click()}
                    className="db-upload-zone"
                    style={{ border: `2px dashed ${resumeText ? "#86efac" : "#cbd5e1"}`, borderRadius: 14, padding: "40px 24px", textAlign: "center", cursor: "pointer", background: resumeText ? "#f0fdf4" : "#f8fafc", transition: "all 0.2s" }}
                    onMouseEnter={e => { if (!resumeText) { e.currentTarget.style.borderColor = "#93c5fd"; e.currentTarget.style.background = "#eff6ff"; } }}
                    onMouseLeave={e => { if (!resumeText) { e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.background = "#f8fafc"; } }}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.background = "#eff6ff"; }}
                    onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.background = "#f8fafc"; const f = e.dataTransfer.files[0]; if (f) handleFileUpload({ target: { files: [f] } }); }}
                  >
                    {resumeText ? (
                      <>
                        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, margin: "0 auto 14px" }}>✅</div>
                        <div style={{ fontWeight: 700, color: "#059669", fontSize: 15, marginBottom: 4 }}>{resumeFileName}</div>
                        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>{resumeText.split(" ").length} words successfully extracted</div>
                        <button onClick={e => { e.stopPropagation(); setResumeText(""); setResumeFileName(""); }}
                          style={{ fontSize: 12, color: "#64748b", background: "#f1f5f9", border: "none", borderRadius: 8, padding: "6px 14px", cursor: "pointer" }}>
                          ✕ Remove & re-upload
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}><Icon name="upload" size={26} color="#2563eb" strokeWidth={1.5} /></div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", marginBottom: 6 }}>Drop your resume here</div>
                        <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 14 }}>or click to browse your files</div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 9, padding: "8px 16px", fontSize: 13, color: "#475569", fontWeight: 500 }}>
                          <span>📎</span> Choose File
                        </div>
                        <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 14 }}>Supports Word (.docx) and plain text (.txt)</div>
                      </>
                    )}
                    <input ref={fileInputRef} type="file" accept=".doc,.docx,.txt" style={{ display: "none" }} onChange={handleFileUpload} />
                  </div>
                ) : (
                  <div style={{ position: "relative" }}>
                    {(() => {
                      const overLimit = resumeText.length > inputLimits.maxResumeLength;
                      const pct = Math.min(100, Math.round((resumeText.length / inputLimits.maxResumeLength) * 100));
                      const barColor = overLimit ? "#dc2626" : pct > 85 ? "#d97706" : "#059669";
                      return (
                        <>
                          <textarea
                            style={{ width: "100%", height: 220, resize: "vertical", fontSize: 13, padding: "14px 16px", border: `1.5px solid ${overLimit ? "#fca5a5" : resumeText ? "#86efac" : "#e2e8f0"}`, borderRadius: 12, fontFamily: "inherit", lineHeight: 1.7, outline: "none", color: "#1e293b", boxSizing: "border-box", background: overLimit ? "#fff5f5" : resumeText ? "#f0fdf4" : "#fff", transition: "all 0.2s" }}
                            placeholder="Paste your full resume text here..."
                            value={resumeText}
                            onChange={e => setResumeText(e.target.value)}
                          />
                          <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              {/* Progress bar */}
                              <div style={{ height: 3, background: "#e2e8f0", borderRadius: 2, overflow: "hidden", marginBottom: 4 }}>
                                <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 2, transition: "width 0.2s, background 0.2s" }} />
                              </div>
                              <div style={{ fontSize: 11, color: overLimit ? "#dc2626" : resumeText ? "#059669" : "#94a3b8", fontWeight: 600 }}>
                                {resumeText
                                  ? overLimit
                                    ? `⚠️ ${resumeText.length.toLocaleString()} / ${inputLimits.maxResumeLength.toLocaleString()} chars — over by ${(resumeText.length - inputLimits.maxResumeLength).toLocaleString()}`
                                    : `✅ ${resumeText.length.toLocaleString()} / ${inputLimits.maxResumeLength.toLocaleString()} chars · ${resumeText.split(" ").filter(Boolean).length} words`
                                  : `Max ${inputLimits.maxResumeLength.toLocaleString()} characters`}
                              </div>
                            </div>
                            {resumeText && <button onClick={() => setResumeText("")} style={{ fontSize: 11, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}>Clear</button>}
                          </div>
                          {overLimit && (
                            <div style={{ marginTop: 6, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#dc2626", lineHeight: 1.6 }}>
                              ⚠️ Resume is too long. Please shorten it by removing older roles, condensing bullet points, or cutting less relevant sections.
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* ── JOB DESCRIPTION CARD ── */}
            <div style={{ background: "#ffffff", borderRadius: 20, marginBottom: 24, border: jdText ? "2px solid #bfdbfe" : "1.5px solid #e2e8f0", transition: "border-color 0.3s", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>

              {/* Card header */}
              <div style={{ padding: "18px 24px 16px", borderBottom: "1px solid #f1f5f9", background: jdText ? "linear-gradient(135deg,#eff6ff,#e0f2fe)" : "#fafaf9" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: jdText ? "#dbeafe" : "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon name={jdText?"target":"clipboard"} size={18} color={jdText?"#2563eb":"#2563eb"} strokeWidth={1.7} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Job Description</div>
                    <div style={{ fontSize: 11, color: jdText ? "#2563eb" : "#94a3b8" }}>
                      {jdText ? `${jdText.split(" ").length} words · AI will extract keywords` : "Paste the job posting you're applying for"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card body */}
              <div style={{ padding: "20px 24px" }}>
                {(() => {
                  const jdOver = jdText.length > inputLimits.maxJdLength;
                  const jdPct  = Math.min(100, Math.round((jdText.length / inputLimits.maxJdLength) * 100));
                  const jdBar  = jdOver ? "#dc2626" : jdPct > 85 ? "#d97706" : "#2563eb";
                  return (
                    <>
                      <textarea
                        style={{ width: "100%", height: 220, resize: "vertical", fontSize: 13, padding: "14px 16px", border: `1.5px solid ${jdOver ? "#fca5a5" : jdText ? "#bfdbfe" : "#e2e8f0"}`, borderRadius: 12, fontFamily: "inherit", lineHeight: 1.7, outline: "none", color: "#1e293b", boxSizing: "border-box", background: jdOver ? "#fff5f5" : jdText ? "#eff6ff" : "#fff", transition: "all 0.2s" }}
                        placeholder="Paste the full job description here — include requirements, responsibilities and skills..."
                        value={jdText}
                        onChange={e => setJdText(e.target.value)}
                      />
                      <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ height: 3, background: "#e2e8f0", borderRadius: 2, overflow: "hidden", marginBottom: 4 }}>
                            <div style={{ height: "100%", width: `${jdPct}%`, background: jdBar, borderRadius: 2, transition: "width 0.2s, background 0.2s" }} />
                          </div>
                          <div style={{ fontSize: 11, color: jdOver ? "#dc2626" : jdText ? "#2563eb" : "#94a3b8", fontWeight: 600 }}>
                            {jdText
                              ? jdOver
                                ? `⚠️ ${jdText.length.toLocaleString()} / ${inputLimits.maxJdLength.toLocaleString()} chars — over by ${(jdText.length - inputLimits.maxJdLength).toLocaleString()}`
                                : `🎯 ${jdText.length.toLocaleString()} / ${inputLimits.maxJdLength.toLocaleString()} chars · ${jdText.split(" ").filter(Boolean).length} words`
                              : `Max ${inputLimits.maxJdLength.toLocaleString()} characters`}
                          </div>
                        </div>
                        {jdText && <button onClick={() => setJdText("")} style={{ fontSize: 11, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}>Clear</button>}
                      </div>
                      {jdOver && (
                        <div style={{ marginTop: 6, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#dc2626", lineHeight: 1.6 }}>
                          ⚠️ Job description is too long. Try pasting only the key sections: responsibilities, requirements, and qualifications.
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* ── ANALYZE BUTTON ── */}
            <div style={{ position: "relative" }}>
              {/* Over-limit warning above button */}
              {(resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength) && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 16px", marginBottom: 12, fontSize: 13, color: "#dc2626", display: "flex", alignItems: "center", gap: 8 }}>
                  <span>⚠️</span>
                  <span>
                    {resumeText.length > inputLimits.maxResumeLength && jdText.length > inputLimits.maxJdLength
                      ? "Both resume and job description are too long."
                      : resumeText.length > inputLimits.maxResumeLength
                      ? `Resume is too long — ${(resumeText.length - inputLimits.maxResumeLength).toLocaleString()} characters over the ${inputLimits.maxResumeLength.toLocaleString()} limit.`
                      : `Job description is too long — ${(jdText.length - inputLimits.maxJdLength).toLocaleString()} characters over the ${inputLimits.maxJdLength.toLocaleString()} limit.`}
                    {" "}Please shorten before analyzing.
                  </span>
                </div>
              )}
              {/* Readiness check */}
              {(!resumeText || !jdText) && !analysisComplete && (
                <div className="db-readiness" style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: resumeText ? "#059669" : "#94a3b8", fontWeight: 500 }}>
                    <span style={{ width: 18, height: 18, borderRadius: "50%", background: resumeText ? "#dcfce7" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>{resumeText ? "✓" : "1"}</span>
                    Resume {resumeText ? "ready" : "required"}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: jdText ? "#059669" : "#94a3b8", fontWeight: 500 }}>
                    <span style={{ width: 18, height: 18, borderRadius: "50%", background: jdText ? "#dcfce7" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>{jdText ? "✓" : "2"}</span>
                    Job description {jdText ? "ready" : "required"}
                  </div>
                </div>
              )}

              <button
                onClick={handleAnalyze}
                disabled={analyzing || analysisComplete || resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength}
                style={{
                  width: "100%", padding: "18px 24px",
                  fontSize: 16, fontWeight: 700,
                  background: analysisComplete
                    ? "linear-gradient(135deg,#059669,#047857)"
                    : analyzing
                    ? "#94a3b8"
                    : (resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength)
                    ? "#fee2e2"
                    : (!resumeText || !jdText)
                    ? "#cbd5e1"
                    : "linear-gradient(135deg,#2563eb,#1d4ed8)",
                  color: (resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength) ? "#dc2626" : "#fff",
                  border: (resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength) ? "1.5px solid #fca5a5" : "none",
                  borderRadius: 14,
                  cursor: (analyzing || analysisComplete || resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength || !resumeText || !jdText) ? "not-allowed" : "pointer",
                  letterSpacing: "-0.01em",
                  boxShadow: (analysisComplete || analyzing || !resumeText || !jdText || resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength) ? "none" : "0 6px 20px rgba(37,99,235,0.35)",
                  transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                }}
                onMouseEnter={e => { if (!analyzing && !analysisComplete && resumeText && jdText && resumeText.length <= inputLimits.maxResumeLength && jdText.length <= inputLimits.maxJdLength) e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
              >
                {analysisComplete ? (
                  <><Icon name="checkCircle" size={18} color="#fff" strokeWidth={2} /> Analysis Complete — Results Ready</>
                ) : analyzing ? (
                  <><Icon name="clock" size={18} color="#fff" strokeWidth={2} /> Analyzing your resume...</>
                ) : (resumeText.length > inputLimits.maxResumeLength || jdText.length > inputLimits.maxJdLength) ? (
                  <><Icon name="alertTriangle" size={18} color="#dc2626" strokeWidth={2} /> Shorten text above before analyzing</>
                ) : (!resumeText || !jdText) ? (
                  <><Icon name="lock" size={18} color="#94a3b8" strokeWidth={2} /> Complete both fields above to analyze</>
                ) : (
                  <>
                    <span style={{ fontSize: 18 }}>🚀</span>
                    <span>{servicesConfig.app.ctaHero}</span>
                    <span className="db-analyze-badge" style={{ background: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>AI-Powered</span>
                  </>
                )}
              </button>

              {!analysisComplete && !analyzing && resumeText && jdText && (
                <p style={{ textAlign: "center", fontSize: 12, color: "#94a3b8", marginTop: 10 }}>
                  🔒 Secure · Takes ~20 seconds · Results saved to your account
                </p>
              )}
            </div>
          </>
        )}

        {/* ── Career mismatch warning ── */}
        {/* ── Career field mismatch warning ── */}
        {mismatchWarning && !mismatchDismissed && (
          <div style={{
            background: "#FAEEDA", border: "1px solid #FAC775",
            borderRadius: 14, padding: "16px 18px", marginBottom: 16,
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "#FAC775", display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0, fontSize: 18,
              }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#412402", marginBottom: 6 }}>
                  Your resume and job description don't seem to match
                </div>
                <p style={{ fontSize: 13, color: "#633806", margin: "0 0 10px", lineHeight: 1.7 }}>
                  It looks like your resume is from the <strong>{mismatchWarning.resumeField}</strong> field, 
                  but the job you're applying for is in <strong>{mismatchWarning.jdField}</strong>. 
                  Because of this, a lot of the keywords in the job posting simply won't appear 
                  in your resume — which will push your ATS score down, not because your resume 
                  is weak, but because it's written for a different industry.
                </p>
                <p style={{ fontSize: 13, color: "#633806", margin: "0 0 12px", lineHeight: 1.7 }}>
                  If this was a mistake, clearing the job description and pasting the right one 
                  will give you a much more useful result. That said, you're welcome to continue 
                  as-is — we'll still run the full analysis and you can generate a resume from it.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={() => { setMismatchDismissed(true); setMismatchWarning(null); handleAnalyze(); }}
                    style={{
                      fontSize: 12, padding: "7px 14px", borderRadius: 8,
                      border: "1px solid #BA7517", background: "#fff",
                      color: "#633806", cursor: "pointer", fontWeight: 600,
                    }}
                  >
                    I know, analyze it anyway →
                  </button>
                  <button
                    onClick={() => { setMismatchWarning(null); setJdText(""); }}
                    style={{
                      fontSize: 12, padding: "7px 14px", borderRadius: 8,
                      border: "none", background: "#854F0B",
                      color: "#fff", cursor: "pointer", fontWeight: 600,
                    }}
                  >
                    Clear job description and re-enter
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ANALYZING OVERLAY ── */}
        {step === "analyzing" && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 1100,
            background: "rgba(15,23,42,0.75)",
            backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}>
            <div style={{
              background: "#ffffff", borderRadius: 24,
              padding: "48px 40px", maxWidth: 460, width: "100%",
              textAlign: "center",
              boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
              animation: "fadeIn 0.3s ease",
            }}>
              {/* Animated logo */}
              <div style={{ position: "relative", width: 80, height: 80, margin: "0 auto 28px" }}>
                <div style={{ width: 80, height: 80, borderRadius: "50%", border: "4px solid #e2e8f0", borderTopColor: "#2563eb", animation: "spin 0.8s linear infinite", position: "absolute", inset: 0 }} />
                <div style={{ width: 80, height: 80, borderRadius: "50%", border: "4px solid transparent", borderBottomColor: "#1d4ed8", animation: "spin 1.2s linear infinite reverse", position: "absolute", inset: 0 }} />
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🎯</div>
              </div>

              <h2 style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", marginBottom: 10, letterSpacing: "-0.02em" }}>
                Analyzing your resume...
              </h2>
              <p style={{ fontSize: 14, color: "#64748b", marginBottom: 32, lineHeight: 1.6 }}>
                Our AI is scanning keywords, scoring ATS compatibility, and preparing your full report.
              </p>

              {/* Animated progress steps */}
              <AnalysisSteps />

              <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 28 }}>
                This usually takes 15–20 seconds
              </p>
            </div>
          </div>
        )}

        {/* ── RESULTS ── */}
        {step === "results" && result && (
          <>
            {/* ══ COMPACT TOP BAR: score + actions in one row ══ */}
            <div style={{ background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 16, padding: "14px 18px", marginBottom: 14, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>

              {/* Score pill */}
              {/* {(() => {
                const r = safeResult(result);
                const sc = r.ats_score;
                const col = sc >= 75 ? "#059669" : sc >= 50 ? "#d97706" : "#dc2626";
                const bg  = sc >= 75 ? "#ecfdf5" : sc >= 50 ? "#fffbeb" : "#fef2f2";
                return (
                  <div style={{ background: bg, borderRadius: 12, padding: "8px 16px", textAlign: "center", flexShrink: 0 }}>
                    <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: col, fontWeight: 700 }}>ATS Score</div>
                    <div style={{ fontSize: 36, fontWeight: 900, color: col, lineHeight: 1 }}>{sc}</div>
                    <div style={{ fontSize: 10, color: col, opacity: 0.7 }}>/100</div>
                  </div>
                );
              })()} */}
              {/* new code */}
              {/* Score ring — animated reveal */}
              <ScoreRing score={safeResult(result).ats_score} animate={true} size={120} />

              {/* Quick stats */}
              {(() => {
                const r = safeResult(result);
                return (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flex: 1 }}>
                    <div style={{ background: "#eff6ff", borderRadius: 9, padding: "6px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#2563eb", fontWeight: 700, textTransform: "uppercase" }}>Match</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#2563eb" }}>{r.match_rate}%</div>
                    </div>
                    <div style={{ background: "#fef2f2", borderRadius: 9, padding: "6px 12px", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#dc2626", fontWeight: 700, textTransform: "uppercase" }}>Missing</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#dc2626" }}>{r.missing_count}</div>
                    </div>
                    {txUsage && (
                      <div style={{ background: "#f8fafc", borderRadius: 9, padding: "6px 12px", textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Analyses</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: "#374151" }}>{txUsage.analysisUsedCount}/{txUsage.analysisCount}</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
                {txUsage?.canAnalyzeAgain && !txUsage?.resumeGenerated && (
                  <button
                    onClick={() => { setResumeText(""); setResumeFileName(""); setJdText(""); setResult(null); setAnalysisComplete(false); setError(""); setBannerType("pending"); setStep("upload"); }}
                    style={{ padding: "8px 14px", background: "#fff", color: "#2563eb", border: "1.5px solid #2563eb", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                  >
                    🔄 Re-analyze
                    <span style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 800 }}>{txUsage.analysisRemaining}</span>
                  </button>
                )}
                {txUsage?.analysisUsedCount > 0 && !txUsage?.resumeGenerated && (
                  <button
                    onClick={() => setShowAnalysisModal(true)}
                    style={{ padding: "8px 14px", background: "linear-gradient(135deg,#059669,#047857)", color: "#fff", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer", boxShadow: "0 3px 10px rgba(5,150,105,0.25)", display: "flex", alignItems: "center", gap: 5 }}
                    onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                    onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}
                  >
                    ✨ Generate Resume
                  </button>
                )}
                {txUsage?.resumeGenerated && (
                  <button onClick={() => handleGetStarted()}
                    style={{ padding: "8px 14px", background: "linear-gradient(135deg,#2563eb,#1d4ed8)", color: "#fff", border: "none", borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
                    💳 New Plan
                  </button>
                )}
                <button onClick={handleDownload}
                  style={{ padding: "8px 14px", background: "#f8fafc", color: "#475569", border: "1.5px solid #e2e8f0", borderRadius: 9, fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.color = "#2563eb"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; }}
                >
                  ↓ Report
                </button>
              </div>
            </div>

            {/* ══ SCORE BAR ══ */}
            {(() => {
              const sc = safeResult(result).ats_score;
              const col = sc >= 75 ? "#059669" : sc >= 50 ? "#d97706" : "#dc2626";
              return (
                <div style={{ height: 6, background: "#e2e8f0", borderRadius: 3, overflow: "hidden", marginBottom: 14 }}>
                  <div style={{ height: "100%", width: `${sc}%`, background: `linear-gradient(90deg,${col},${col}cc)`, borderRadius: 3, transition: "width 0.8s ease" }} />
                </div>
              );
            })()}

            {/* ══ SUMMARY (if exists — compact) ══ */}
            {safeResult(result).summary && (
              <div style={{ background: "#eff6ff", borderLeft: "4px solid #2563eb", padding: "14px 18px", borderRadius: 10, marginBottom: 14, fontSize: 14, color: "#1e40af", lineHeight: 1.7 }}>
                {safeResult(result).summary}
              </div>
            )}

            {/* ══ 2-COLUMN LAYOUT: keywords + sections side by side ══ */}
            <div className="db-results-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>

              {/* Keywords found */}
              <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#059669", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="checkCircle" size={14} color="#059669" strokeWidth={2.2} /> Keywords Found <span style={{ background: "#d1fae5", color: "#065f46", fontSize: 11, padding: "2px 9px", borderRadius: 20, fontWeight: 700 }}>{safeResult(result).present_keywords.length}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 150, overflowY: "auto" }}>
                  {safeResult(result).present_keywords.length > 0
                    ? safeResult(result).present_keywords.map(k => (
                        <span key={k} style={{ background: "#d1fae5", color: "#065f46", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{k}</span>
                      ))
                    : <span style={{ fontSize: 13, color: "#94a3b8" }}>None detected</span>}
                </div>
              </div>

              {/* Keywords missing */}
              <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon name="xCircle" size={14} color="#dc2626" strokeWidth={2.2} /> Missing Keywords <span style={{ background: "#fee2e2", color: "#dc2626", fontSize: 11, padding: "2px 9px", borderRadius: 20, fontWeight: 700 }}>{safeResult(result).missing_keywords.length}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 150, overflowY: "auto" }}>
                  {safeResult(result).missing_keywords.length > 0
                    ? safeResult(result).missing_keywords.map(k => (
                        <span key={k} style={{ background: "#fee2e2", color: "#dc2626", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{k}</span>
                      ))
                    : <span style={{ fontSize: 13, color: "#94a3b8" }}>No major gaps</span>}
                </div>
              </div>
            </div>

            {/* ══ SECTION FEEDBACK ══ */}
            {safeResult(result).sections.length > 0 && (
              <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", marginBottom: 12, border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 14 }}>Section Feedback</div>
                {/* <div className="db-sf-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}> */}
                {/* //NEW CODE */}
                <div className="db-sf-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                  {safeResult(result).sections.map((s, i) => {
                    const c = s.score === "Good" ? "#059669" : s.score === "Fair" ? "#d97706" : "#dc2626";
                    const b = s.score === "Good" ? "#ecfdf5" : s.score === "Fair" ? "#fffbeb" : "#fef2f2";
                    return (
                      <div key={i} style={{ background: "#f8fafc", borderRadius: 12, padding: "14px 16px", borderLeft: `4px solid ${c}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                          <span style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{s.name}</span>
                          <span style={{ background: b, color: c, fontSize: 11, padding: "2px 10px", borderRadius: 20, fontWeight: 700 }}>{s.score}</span>
                        </div>
                        <p style={{ fontSize: 13, color: "#475569", margin: 0, lineHeight: 1.6 }}>{s.feedback}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ══ REWRITES + RECOMMENDATIONS — 2 col ══ */}
            <div className="db-results-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>

              {/* Rewrites */}
              {safeResult(result).rewrites.length > 0 && (
                <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Suggested Rewrites</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 280, overflowY: "auto" }}>
                    {safeResult(result).rewrites.map((r, i) => (
                      <div key={i}>
                        {r.section && <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>{r.section}</div>}
                        <div style={{ fontSize: 13, color: "#475569", padding: "10px 12px", background: "#f8fafc", borderRadius: 8, marginBottom: 4, border: "1px solid #e2e8f0", lineHeight: 1.6 }}>{r.before}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", margin: "3px 0" }}>↓</div>
                        <div style={{ fontSize: 13, color: "#2563eb", padding: "10px 12px", background: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe", lineHeight: 1.6 }}>{r.after}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {safeResult(result).top_recommendations.length > 0 && (
                <div style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 12 }}>Top Recommendations</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 280, overflowY: "auto" }}>
                    {safeResult(result).top_recommendations.map((r, i) => (
                      <div key={i} style={{ padding: "10px 14px", background: "#fffbeb", borderRadius: 9, fontSize: 13, display: "flex", gap: 10, alignItems: "flex-start", border: "1px solid #fde68a" }}>
                        <span style={{ color: "#d97706", fontWeight: 800, minWidth: 18, fontSize: 12 }}>{i + 1}.</span>
                        <span style={{ color: "#334155", lineHeight: 1.6, fontSize: 13 }}>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ══ FOOTER ACTIONS ══ */}
            <div className="db-bottom-btns" style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 8, flexWrap: "wrap" }}>
              <button onClick={handleDownload}
                style={{ padding: "10px 24px", background: "transparent", color: "#2563eb", border: "2px solid #2563eb", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#2563eb"; e.currentTarget.style.color = "#fff"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#2563eb"; }}>
                ↓ Download Report
              </button>
              <button onClick={handleGoToDashboard}
                style={{ padding: "10px 24px", background: "transparent", color: "#475569", border: "2px solid #cbd5e1", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "#475569"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "#cbd5e1"}>
                View History
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── SERVICE SELECTOR MODAL ── */}
      {showServiceSelector && (
        <ServiceSelectorModal
          onSelect={handleServiceSelected}
          onClose={() => setShowServiceSelector(false)}
        />
      )}

      {/* ── ANALYSIS SELECT + TEMPLATE MODAL ── */}
      {showAnalysisModal && (
        <AnalysisSelectModal
          analyses={txAnalyses.length > 0 ? txAnalyses : allAnalyses.filter(a => a.transactionId === transactionId)}
          transactionId={transactionId}
          planId={txUsage?.planId || selectedPlanId}
          onClose={() => setShowAnalysisModal(false)}
        />
      )}

      {/* ── PAYMENT MODAL ── */}
      {showPayModal && (
        <PaymentModal
          onSuccess={handlePaymentSuccess}
          onClose={() => setShowPayModal(false)}
          userEmail={userData?.email || user?.email}
          userName={`${userData?.firstName || ""} ${userData?.lastName || ""}`.trim()}
          planId={selectedPlanId}
          userId={user?.uid}
        />
      )}

      <style>{`
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-4px); opacity: 1; }
        }

        /* ── TABLET 768px ── */
        @media (max-width: 768px) {
          /* Nav */
          .db-nav-name { display: none !important; }
          .db-nav-btn { padding: 6px 10px !important; gap: 6px !important; }

          /* Layout */
          .db-main { padding: 20px 3% !important; }

          /* Intro / Plans */
          .db-svc-grid { grid-template-columns: 1fr !important; }
          .db-steps-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }

          /* Upload page */
          .db-upload-card { border-radius: 14px !important; }
          .db-upload-card-header { flex-direction: column !important; align-items: flex-start !important; gap: 10px !important; }
          .db-upload-toggle { width: 100% !important; justify-content: stretch !important; }
          .db-upload-toggle button { flex: 1 !important; text-align: center !important; }
          .db-upload-zone { padding: 28px 16px !important; }
          .db-step-bar { gap: 4px !important; }
          .db-step-bar-label { display: none !important; }
          .db-readiness { flex-wrap: wrap !important; gap: 6px !important; }

          /* History */
          .db-history-banner { flex-direction: column !important; align-items: flex-start !important; }
          .db-history-banner button { width: 100% !important; }
          .db-hcard-header { flex-direction: column !important; align-items: flex-start !important; }
          .db-hcard-actions { width: 100% !important; display: flex !important; }
          .db-hcard-actions button { flex: 1 !important; }

          /* Results */
          .db-metrics-3 { grid-template-columns: repeat(3, 1fr) !important; }
          .db-kw-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
          .db-results-header { flex-direction: column !important; align-items: flex-start !important; }
          .db-results-header-btns { width: 100% !important; display: flex !important; }
          .db-results-header-btns button { flex: 1 !important; }
          .db-bottom-btns { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .db-bottom-btns button { padding: 11px 8px !important; font-size: 13px !important; }
        }

        /* ── MOBILE 480px ── */
        @media (max-width: 480px) {
          .db-main { padding: 16px 4% !important; }

          /* Plans grid stacks */
          .db-svc-grid { grid-template-columns: 1fr !important; gap: 14px !important; }

          /* Step bar — show only icons + numbers, hide text labels */
          .db-step-bar { gap: 2px !important; }

          /* Upload zone more compact */
          .db-upload-zone { padding: 22px 12px !important; }

          /* Results grids */
          .db-results-grid { grid-template-columns: 1fr !important; }
          .db-sf-grid { grid-template-columns: 1fr !important; }

          /* Results metrics — 1 column */
          .db-metrics-3 { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
          .db-bottom-btns { grid-template-columns: 1fr !important; }

          /* Analyze button text shorter on very small screens */
          .db-analyze-badge { display: none !important; }
        }

        /* ── TINY 360px ── */
        @media (max-width: 360px) {
          .db-metrics-3 { grid-template-columns: 1fr !important; }
          .db-hcard-actions { flex-direction: column !important; }
          .db-hcard-actions button { width: 100% !important; }
        }
      `}</style>
    </div>
  );
}

